import { useId, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { describeError } from '../../lib/describeError'
import { createBrand, listBrands, type BrandRef } from '../api'
import { addLabel, BRAND_HELP, brandOptions, brandsKey, optionLabel, type BrandOption } from './brandPicker'
import { btnSmall, errorText, field, focus } from './ui'

type Props = {
  publicationSlug: string
  brand: BrandRef | null
  /** False while the piece can't change (read-only, a check running, a writer's live piece). */
  editable: boolean
  /** Save the pick (null clears it); resolves once the piece has saved. */
  onPick: (brandId: number | null) => Promise<void>
}

/** Pit Lane brand pages: the brand a piece is filed under. Type to find; an unknown name can be
 *  added as a new brand. Shown only when the title's switch is on (the page decides). */
export function BrandPicker({ publicationSlug, brand, editable, onPick }: Props) {
  const qc = useQueryClient()
  const id = useId()
  const [text, setText] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const brands = useQuery({ queryKey: brandsKey(publicationSlug), queryFn: () => listBrands(publicationSlug), staleTime: 60_000 })
  const pick = useMutation({
    mutationFn: async (o: BrandOption | null) => {
      if (o === null) return onPick(null)
      const b = o.kind === 'brand' ? o.brand : await createBrand(publicationSlug, o.name)
      if (o.kind === 'add') void qc.invalidateQueries({ queryKey: brandsKey(publicationSlug) })
      await onPick(b.id)
    },
    onSuccess: () => { setText(''); setOpen(false); setActive(0) },
  })
  const options = brandOptions(brands.data ?? [], text)
  const busy = pick.isPending
  const choose = (o: BrandOption | undefined) => { if (o && !busy) pick.mutate(o) }

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={`${id}-input`} className="font-semibold">Brand</label>
      {brand ? (
        <div className="flex items-center gap-2">
          <span className="inline-flex min-h-9 items-center rounded-full bg-ink-600 px-3 text-[14px] font-semibold text-paper">{brand.name}</span>
          {editable && (
            <button type="button" className={btnSmall} aria-label={`Clear the brand ${brand.name}`} disabled={busy}
                    onClick={() => pick.mutate(null)}>×</button>
          )}
        </div>
      ) : (
        <div>
          <input id={`${id}-input`} type="text" className={field} value={text} disabled={!editable || busy}
                 role="combobox" aria-expanded={open && options.length > 0} aria-controls={`${id}-list`} aria-autocomplete="list"
                 aria-activedescendant={open && options[active] ? `${id}-opt-${active}` : undefined}
                 aria-describedby={`${id}-help`} placeholder="Type to find a brand" autoComplete="off"
                 onChange={(e) => { setText(e.target.value); setOpen(true); setActive(0) }}
                 onFocus={() => setOpen(true)}
                 onBlur={() => setOpen(false)}
                 onKeyDown={(e) => {
                   if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, options.length - 1)) }
                   else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)) }
                   else if (e.key === 'Enter' && open) { e.preventDefault(); choose(options[active]) }
                   else if (e.key === 'Escape') setOpen(false)
                 }} />
          {open && editable && options.length > 0 && (
            <ul id={`${id}-list`} role="listbox" aria-label="Brands"
                className="m-0 mt-1 max-h-72 list-none overflow-y-auto rounded-lg border border-ink-400 bg-ink-700 p-1 shadow-lg">
              {options.map((o, i) => (
                <li key={o.kind === 'brand' ? o.brand.id : 'add'} id={`${id}-opt-${i}`} role="option" aria-selected={i === active}
                    // mousedown, not click: the input's blur would close the list first (it sits in
                    // the flow, not floating: the side panel clips what overflows it)
                    onMouseDown={(e) => { e.preventDefault(); choose(o) }} onMouseEnter={() => setActive(i)}
                    className={`cursor-pointer rounded-md px-3 py-2 text-[14px] ${focus} ${i === active ? 'bg-ink-600 text-paper' : 'text-paper-dim'} ${
                      o.kind === 'add' ? 'font-semibold text-amber-deep' : ''}`}>
                  {o.kind === 'brand' ? optionLabel(o.brand) : addLabel(o.name)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <span id={`${id}-help`} className="text-[12px] text-paper-faint">{BRAND_HELP}</span>
      {brands.isError && <p className={errorText}>Could not load the brands. {describeError(brands.error)}</p>}
      {pick.isError && <p role="alert" className={errorText}>{describeError(pick.error)}</p>}
    </div>
  )
}
